import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Toaster, toast } from "sonner";
import { LogIn, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user && user !== false) {
    const landing = (user.role === "admin" || user.role === "manager") ? "/admin" : "/scan";
    return <Navigate to={landing} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (!res.ok) {
      setErr(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Welcome, ${res.user.name}`);
    const landing = (res.user.role === "admin" || res.user.role === "manager") ? "/admin" : "/scan";
    nav(landing, { replace: true });
  };

  return (
    <div className="min-h-screen relative flex items-stretch bg-paper" data-testid="login-page">
      {/* Right-side brand panel (desktop only) */}
      <div
        className="hidden md:block absolute inset-y-0 right-0 w-1/2 bg-ink overflow-hidden"
        aria-hidden="true"
      >
        <div
          className="absolute inset-0 opacity-90"
          style={{
            background:
              "radial-gradient(120% 80% at 100% 100%, rgba(255,87,34,0.35) 0%, rgba(255,87,34,0.10) 35%, rgba(15,15,15,0) 70%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 lg:p-16 text-paper">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/60">
            Retail freshness platform
          </div>
          <div>
            <div className="font-display font-black text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
              Shelf<span className="text-brand">Life</span>
            </div>
            <div className="mt-4 w-16 h-0.5 bg-brand" />
            <p className="mt-6 font-display text-lg lg:text-xl text-paper/80 max-w-md leading-snug">
              Reduce food waste before it happens.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 font-mono text-[10px] uppercase tracking-wider text-paper/50">
            <div>Scan · Stock · Remove</div>
            <div>Departmental</div>
            <div className="text-right">Fresh food retail</div>
          </div>
        </div>
      </div>

      <div className="relative z-10 flex flex-col w-full md:w-1/2 md:min-w-[520px] bg-paper px-6 sm:px-12 py-10 md:py-16 pb-32 md:pb-16">
        <div className="flex items-center gap-2 mb-10">
          <div className="h-10 w-10 bg-brand rounded-sm flex items-center justify-center text-white font-display font-black text-xl">
            S
          </div>
          <div className="font-display font-black text-2xl tracking-tight">
            Shelf<span className="text-brand">Life</span>
          </div>
        </div>

        <div className="max-w-md">
          <div className="data-sku mb-3">Sign in</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl leading-[0.95] tracking-tighter mb-4">
            Track every shelf, before it expires.
          </h1>
          <p className="text-base text-foreground/80 mb-10">
            Scan, add, and remove inventory with confidence — department by department.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="data-sku block mb-2" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-14 px-4 bg-input border border-border rounded-sm font-body text-base focus:outline-none focus:border-ink"
                placeholder="you@supermarket.co"
                autoComplete="email"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <label className="data-sku" htmlFor="password">Password</label>
                <Link
                  to="/forgot-password"
                  className="font-mono text-[11px] uppercase tracking-wider text-ink/60 hover:text-ink"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={reveal ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-14 pl-4 pr-12 bg-input border border-border rounded-sm font-body text-base focus:outline-none focus:border-ink"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
                {/*
                  Show/Hide toggle, sits inside the field so it stays
                  with the input on mobile. Toggles ONLY input.type;
                  no value is logged, stored, or transmitted beyond
                  the existing form submit. Keyboard-focusable with
                  visible focus ring; aria-pressed reflects state for
                  screen readers.
                */}
                <button
                  type="button"
                  onClick={() => setReveal((s) => !s)}
                  aria-label={reveal ? "Hide password" : "Show password"}
                  aria-pressed={reveal}
                  tabIndex={0}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-10 w-10 rounded-sm text-ink/60 hover:text-ink hover:bg-paper-sunk focus:outline-none focus-visible:ring-2 focus-visible:ring-brand transition-colors"
                  data-testid="login-password-reveal-toggle"
                >
                  {reveal ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {err && (
              <div
                className="bg-shelf-expired-bg text-shelf-expired px-4 py-3 rounded-sm font-mono text-xs"
                data-testid="login-error"
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit-button"
            >
              <LogIn size={18} /> {loading ? "Signing in…" : "Sign in"}
            </button>

            <div
              className="text-center pt-3 font-mono text-xs uppercase tracking-wider text-ink/60"
              data-testid="login-invite-only-notice"
            >
              Need access?{" "}
              <span className="text-ink">
                Contact your ShelfLife administrator.
              </span>
            </div>
          </form>
        </div>

        <div className="mt-auto pt-10 data-sku">© ShelfLife · Multi-department</div>
      </div>
      <Toaster position="bottom-center" richColors />
    </div>
  );
}
